/**
 * CampaignLorePanel
 *
 * Campaign lore compendium for the GM Assistant. Aggregates existing data
 * from factionStore, npcStore, journalStore, and campaignHistoryStore into
 * a browseable reference. Sub-sections: World, Factions, Districts, Chronicle.
 */

import { useState } from 'react';
import { Icon, type IconName } from '../Icon';
import { useFactionStore } from '../../stores/factionStore';
import { useNpcStore } from '../../stores/npcStore';
import { useJournalStore } from '../../stores/journalStore';
import { useCampaignHistoryStore } from '../../stores/campaignHistoryStore';
import { GRIST_COSMOLOGY, GRIST_DISTRICTS, GRIST_NPCS } from '@machina/core';
import type { Faction, FactionNpc, District, Landmark } from '@machina/core';

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.4)';
const SILVER = 'rgba(192, 192, 192, 0.7)';
const BG_DARK = 'rgba(10, 8, 12, 0.6)';
const BG_CARD = 'rgba(30, 25, 35, 0.8)';
const BORDER = 'rgba(212, 175, 55, 0.15)';

type Section = 'world' | 'factions' | 'districts' | 'chronicle';

const SECTIONS: { id: Section; label: string; icon: IconName }[] = [
  { id: 'world', label: 'World', icon: 'compass' },
  { id: 'factions', label: 'Factions', icon: 'flag' },
  { id: 'districts', label: 'Districts', icon: 'map' },
  { id: 'chronicle', label: 'Chronicle', icon: 'scroll' },
];

interface Props {
  fontScale: number;
}

export function CampaignLorePanel({ fontScale }: Props) {
  const [section, setSection] = useState<Section>('world');
  const baseFontSize = 12 + fontScale;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Section tabs */}
      <div style={{
        display: 'flex', gap: 2, padding: '8px 12px 4px',
        borderBottom: `1px solid ${BORDER}`,
      }}>
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => setSection(s.id)}
            style={{
              background: section === s.id ? 'rgba(212, 175, 55, 0.12)' : 'none',
              border: section === s.id ? `1px solid ${GOLD_DIM}` : '1px solid transparent',
              borderRadius: 4,
              padding: '4px 10px',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 4,
              fontFamily: 'Georgia, serif',
              fontSize: baseFontSize - 1,
              color: section === s.id ? GOLD : SILVER,
              transition: 'all 0.2s',
            }}
          >
            <Icon name={s.icon} size={11} color={section === s.id ? GOLD : SILVER} />
            {s.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
        {section === 'world' && <WorldSection fontSize={baseFontSize} />}
        {section === 'factions' && <FactionsSection fontSize={baseFontSize} />}
        {section === 'districts' && <DistrictsSection fontSize={baseFontSize} />}
        {section === 'chronicle' && <ChronicleSection fontSize={baseFontSize} />}
      </div>
    </div>
  );
}

// ─── World Section ────────────────────────────────────────────

function WorldSection({ fontSize }: { fontSize: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Cosmology */}
      <Card title={GRIST_COSMOLOGY.name} icon="star" fontSize={fontSize}>
        <p style={pStyle(fontSize)}>{GRIST_COSMOLOGY.summary}</p>
        <div style={{ marginTop: 8 }}>
          <LoreLabel>Key Facts</LoreLabel>
          <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
            {GRIST_COSMOLOGY.keyFacts.map((fact, i) => (
              <li key={i} style={{ ...pStyle(fontSize), marginBottom: 4 }}>{fact}</li>
            ))}
          </ul>
        </div>
      </Card>

      {/* The Unraveling */}
      <Card title="The Unraveling" icon="skull" fontSize={fontSize}>
        <p style={pStyle(fontSize)}>{GRIST_COSMOLOGY.theUnraveling}</p>
      </Card>
    </div>
  );
}

// ─── Factions Section ─────────────────────────────────────────

function FactionsSection({ fontSize }: { fontSize: number }) {
  const factions = useFactionStore((s) => s.factions);
  const reputations = useFactionStore((s) => s.reputations);
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {factions.filter((f) => !f.isSecret).map((faction) => {
        const rep = reputations.find((r) => r.factionId === faction.id && r.characterId === 'party');
        const isOpen = expanded === faction.id;
        const npcs = GRIST_NPCS.filter((n) => n.factionId === faction.id && !n.isSecret);

        return (
          <FactionCard
            key={faction.id}
            faction={faction}
            npcs={npcs}
            repScore={rep?.reputationScore}
            repTier={rep?.tier}
            isOpen={isOpen}
            onToggle={() => setExpanded(isOpen ? null : faction.id)}
            fontSize={fontSize}
          />
        );
      })}
    </div>
  );
}

function FactionCard({ faction, npcs, repScore, repTier, isOpen, onToggle, fontSize }: {
  faction: Faction;
  npcs: FactionNpc[];
  repScore?: number;
  repTier?: string;
  isOpen: boolean;
  onToggle: () => void;
  fontSize: number;
}) {
  return (
    <div style={{
      background: BG_CARD,
      border: `1px solid ${isOpen ? faction.color : BORDER}`,
      borderRadius: 6,
      overflow: 'hidden',
      transition: 'border-color 0.2s',
    }}>
      {/* Header — clickable */}
      <button
        onClick={onToggle}
        style={{
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8,
          textAlign: 'left',
        }}
      >
        <div style={{
          width: 10, height: 10, borderRadius: '50%',
          background: faction.color, flexShrink: 0,
        }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'Georgia, serif', fontSize, color: '#e0d5c0', fontWeight: 'bold' }}>
            {faction.name}
          </div>
          <div style={{ fontFamily: 'Georgia, serif', fontSize: fontSize - 2, color: SILVER, fontStyle: 'italic' }}>
            {faction.philosophy}
          </div>
        </div>
        {repTier && (
          <div style={{
            fontSize: fontSize - 3, color: repColor(repTier),
            fontFamily: 'Georgia, serif', textTransform: 'capitalize',
          }}>
            {repTier} {repScore != null && <span style={{ opacity: 0.5 }}>({repScore > 0 ? '+' : ''}{repScore})</span>}
          </div>
        )}
        <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size={12} color={SILVER} />
      </button>

      {/* Expanded content */}
      {isOpen && (
        <div style={{ padding: '0 12px 12px', borderTop: `1px solid ${BORDER}` }}>
          <p style={{ ...pStyle(fontSize), marginTop: 8 }}>{faction.description}</p>

          {/* Ideology position */}
          <div style={{ marginTop: 8, display: 'flex', gap: 16 }}>
            <IdeologyBar label="Order / Entropy" value={faction.ideologyPosition.orderEntropy} color={faction.color} fontSize={fontSize} />
            <IdeologyBar label="Self / Collective" value={faction.ideologyPosition.selfCollective} color={faction.color} fontSize={fontSize} />
          </div>

          {/* Known NPCs */}
          {npcs.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <LoreLabel>Known Members</LoreLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
                {npcs.map((npc) => (
                  <div key={npc.id} style={{
                    display: 'flex', gap: 6, alignItems: 'baseline',
                    fontSize: fontSize - 1, fontFamily: 'Georgia, serif',
                  }}>
                    <span style={{ color: '#e0d5c0', fontWeight: 'bold' }}>{npc.name}</span>
                    {npc.title && <span style={{ color: SILVER, fontStyle: 'italic' }}>— {npc.title}</span>}
                    <span style={{
                      fontSize: fontSize - 3, color: GOLD_DIM,
                      textTransform: 'capitalize', marginLeft: 'auto',
                    }}>
                      {npc.role}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* HQ */}
          {faction.headquarters && (
            <div style={{ marginTop: 8, fontSize: fontSize - 2, color: SILVER }}>
              <Icon name="castle" size={10} color={GOLD_DIM} style={{ marginRight: 4 }} />
              HQ: <span style={{ color: '#e0d5c0' }}>
                {GRIST_DISTRICTS.find((d) => d.id === faction.headquarters)?.name ?? faction.headquarters}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function IdeologyBar({ label, value, color, fontSize }: {
  label: string; value: number; color: string; fontSize: number;
}) {
  // value is -100 to 100, center is 0
  const pct = (value + 100) / 200 * 100;
  return (
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: fontSize - 3, color: SILVER, marginBottom: 2 }}>{label}</div>
      <div style={{ height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 2, position: 'relative' }}>
        <div style={{
          position: 'absolute', left: '50%', top: -1, width: 1, height: 6,
          background: 'rgba(255,255,255,0.2)',
        }} />
        <div style={{
          position: 'absolute',
          left: `${pct}%`, top: -2,
          width: 8, height: 8, borderRadius: '50%',
          background: color, border: '1px solid rgba(255,255,255,0.3)',
          transform: 'translateX(-50%)',
        }} />
      </div>
    </div>
  );
}

// ─── Districts Section ────────────────────────────────────────

function DistrictsSection({ fontSize }: { fontSize: number }) {
  const factions = useFactionStore((s) => s.factions);
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {GRIST_DISTRICTS.map((district) => {
        const isOpen = expanded === district.id;
        const dominant = factions.find((f) => f.id === district.dominantFaction);
        const contested = district.contestedBy.map((id) => factions.find((f) => f.id === id)).filter(Boolean) as Faction[];

        return (
          <DistrictCard
            key={district.id}
            district={district}
            dominant={dominant}
            contested={contested}
            isOpen={isOpen}
            onToggle={() => setExpanded(isOpen ? null : district.id)}
            fontSize={fontSize}
          />
        );
      })}
    </div>
  );
}

function DistrictCard({ district, dominant, contested, isOpen, onToggle, fontSize }: {
  district: District;
  dominant?: Faction;
  contested: Faction[];
  isOpen: boolean;
  onToggle: () => void;
  fontSize: number;
}) {
  return (
    <div style={{
      background: BG_CARD,
      border: `1px solid ${isOpen ? (dominant?.color ?? GOLD_DIM) : BORDER}`,
      borderRadius: 6,
      overflow: 'hidden',
      transition: 'border-color 0.2s',
    }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8,
          textAlign: 'left',
        }}
      >
        <Icon name="map" size={14} color={dominant?.color ?? GOLD_DIM} />
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'Georgia, serif', fontSize, color: '#e0d5c0', fontWeight: 'bold' }}>
            {district.name}
          </div>
          <div style={{ fontFamily: 'Georgia, serif', fontSize: fontSize - 2, color: SILVER, fontStyle: 'italic' }}>
            {district.epithet}
          </div>
        </div>
        {dominant && (
          <div style={{
            fontSize: fontSize - 3, color: dominant.color,
            fontFamily: 'Georgia, serif',
          }}>
            {dominant.name}
          </div>
        )}
        <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size={12} color={SILVER} />
      </button>

      {isOpen && (
        <div style={{ padding: '0 12px 12px', borderTop: `1px solid ${BORDER}` }}>
          <p style={{ ...pStyle(fontSize), marginTop: 8 }}>{district.description}</p>

          {/* Mood */}
          <div style={{ marginTop: 6, fontSize: fontSize - 2, color: GOLD_DIM, fontStyle: 'italic' }}>
            &ldquo;{district.mood}&rdquo;
          </div>

          {/* Atmosphere */}
          <div style={{ marginTop: 10 }}>
            <LoreLabel>Atmosphere</LoreLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 4 }}>
              <AtmoLine icon="eye" label="Light" text={district.atmosphere.lighting} fontSize={fontSize} />
              <AtmoLine icon="horn" label="Sound" text={district.atmosphere.sounds} fontSize={fontSize} />
              <AtmoLine icon="droplet" label="Smell" text={district.atmosphere.smell} fontSize={fontSize} />
            </div>
          </div>

          {/* Landmarks */}
          {district.landmarks.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <LoreLabel>Landmarks</LoreLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                {district.landmarks.map((lm) => (
                  <LandmarkEntry key={lm.name} landmark={lm} fontSize={fontSize} />
                ))}
              </div>
            </div>
          )}

          {/* Contested by */}
          {contested.length > 0 && !dominant && (
            <div style={{ marginTop: 8, fontSize: fontSize - 2, color: SILVER }}>
              <LoreLabel>Contested By</LoreLabel>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                {contested.map((f) => (
                  <span key={f.id} style={{
                    fontSize: fontSize - 3, color: f.color,
                    background: 'rgba(255,255,255,0.05)',
                    padding: '1px 6px', borderRadius: 3,
                  }}>
                    {f.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Secrets (GM only) */}
          {district.secrets.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <LoreLabel>
                <Icon name="lock" size={9} color={GOLD_DIM} style={{ marginRight: 3 }} />
                GM Secrets
              </LoreLabel>
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                {district.secrets.map((secret, i) => (
                  <li key={i} style={{
                    ...pStyle(fontSize),
                    color: 'rgba(212, 175, 55, 0.6)',
                    marginBottom: 3,
                  }}>
                    {secret}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AtmoLine({ icon, label, text, fontSize }: {
  icon: IconName; label: string; text: string; fontSize: number;
}) {
  return (
    <div style={{ fontSize: fontSize - 2, color: SILVER, display: 'flex', gap: 4 }}>
      <Icon name={icon} size={10} color={GOLD_DIM} style={{ marginTop: 2, flexShrink: 0 }} />
      <span><span style={{ color: GOLD_DIM }}>{label}:</span> {text}</span>
    </div>
  );
}

function LandmarkEntry({ landmark, fontSize }: { landmark: Landmark; fontSize: number }) {
  return (
    <div style={{
      background: BG_DARK, borderRadius: 4, padding: '6px 8px',
      border: `1px solid ${BORDER}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontFamily: 'Georgia, serif', fontSize: fontSize - 1, color: '#e0d5c0', fontWeight: 'bold' }}>
          {landmark.name}
        </span>
        <span style={{ fontSize: fontSize - 3, color: GOLD_DIM, textTransform: 'capitalize' }}>
          {landmark.type}
        </span>
      </div>
      <p style={{ ...pStyle(fontSize), fontSize: fontSize - 2, marginTop: 2 }}>{landmark.description}</p>
    </div>
  );
}

// ─── Chronicle Section ────────────────────────────────────────

function ChronicleSection({ fontSize }: { fontSize: number }) {
  const journal = useJournalStore((s) => s.entries);
  const history = useCampaignHistoryStore((s) => s.events);
  const killLog = useCampaignHistoryStore((s) => s.getKillLog());
  const encounters = useCampaignHistoryStore((s) => s.getEncounterHistory());
  const npcs = useNpcStore((s) => s.npcs);

  const hasJournal = journal.length > 0;
  const hasHistory = history.length > 0;
  const hasNpcs = npcs.length > 0;
  const isEmpty = !hasJournal && !hasHistory && !hasNpcs;

  if (isEmpty) {
    return (
      <div style={{ textAlign: 'center', padding: 32, color: SILVER, fontFamily: 'Georgia, serif', fontSize }}>
        <Icon name="scroll" size={28} color={GOLD_DIM} />
        <div style={{ marginTop: 8, fontStyle: 'italic' }}>No chronicle entries yet.</div>
        <div style={{ fontSize: fontSize - 2, marginTop: 4, color: GOLD_DIM }}>
          Journal entries, combat encounters, and NPC encounters will appear here.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Journal entries */}
      {hasJournal && (
        <Card title="Journal" icon="scroll" fontSize={fontSize}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {journal.slice(0, 20).map((entry) => (
              <div key={entry.id} style={{
                background: BG_DARK, borderRadius: 4, padding: '6px 8px',
                border: `1px solid ${BORDER}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  {entry.title && (
                    <span style={{ fontFamily: 'Georgia, serif', fontSize: fontSize - 1, color: '#e0d5c0', fontWeight: 'bold' }}>
                      {entry.title}
                    </span>
                  )}
                  <span style={{ fontSize: fontSize - 3, color: GOLD_DIM, marginLeft: 'auto' }}>
                    {new Date(entry.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <p style={{ ...pStyle(fontSize), fontSize: fontSize - 2, marginTop: 2 }}>
                  {entry.body.length > 200 ? entry.body.slice(0, 200) + '...' : entry.body}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* NPC encounters */}
      {hasNpcs && (
        <Card title={`NPCs Met (${npcs.filter((n) => n.status === 'active').length} active)`} icon="users" fontSize={fontSize}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {npcs.filter((n) => !n.isSecret).slice(0, 30).map((npc) => (
              <div key={npc.id} style={{
                display: 'flex', gap: 6, alignItems: 'baseline',
                fontSize: fontSize - 1, fontFamily: 'Georgia, serif',
              }}>
                <span style={{ color: '#e0d5c0', fontWeight: 'bold' }}>{npc.name}</span>
                {npc.title && <span style={{ color: SILVER, fontStyle: 'italic' }}>— {npc.title}</span>}
                <span style={{
                  fontSize: fontSize - 3, marginLeft: 'auto',
                  color: npcStatusColor(npc.status),
                  textTransform: 'capitalize',
                }}>
                  {npc.status}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Kill log */}
      {killLog.length > 0 && (
        <Card title={`Bestiary Defeated (${killLog.length})`} icon="skull" fontSize={fontSize}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {killLog.slice(0, 20).map((evt) => {
              const data = evt.data as { mobName?: string; killerName?: string; xpValue?: number };
              return (
                <div key={evt.id} style={{
                  display: 'flex', gap: 6, alignItems: 'baseline',
                  fontSize: fontSize - 1, fontFamily: 'Georgia, serif',
                }}>
                  <Icon name="sword" size={9} color="rgba(200,50,50,0.6)" />
                  <span style={{ color: '#e0d5c0' }}>{data.mobName ?? 'Unknown'}</span>
                  {data.killerName && <span style={{ color: SILVER, fontSize: fontSize - 3 }}>by {data.killerName}</span>}
                  {data.xpValue != null && (
                    <span style={{ fontSize: fontSize - 3, color: GOLD_DIM, marginLeft: 'auto' }}>
                      {data.xpValue} XP
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Encounter summary */}
      {encounters.length > 0 && (
        <Card title={`Encounters (${encounters.length})`} icon="sword" fontSize={fontSize}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {encounters.slice(0, 15).map((evt) => {
              const data = evt.data as { encounterId?: string; sceneName?: string; rounds?: number; totalXp?: number };
              const isEnd = evt.eventType === 'encounter_end';
              return (
                <div key={evt.id} style={{
                  display: 'flex', gap: 6, alignItems: 'baseline',
                  fontSize: fontSize - 1, fontFamily: 'Georgia, serif',
                }}>
                  <span style={{ color: '#e0d5c0' }}>{data.sceneName ?? 'Encounter'}</span>
                  {isEnd && data.rounds != null && (
                    <span style={{ color: SILVER, fontSize: fontSize - 3 }}>{data.rounds} rounds</span>
                  )}
                  {isEnd && data.totalXp != null && (
                    <span style={{ fontSize: fontSize - 3, color: GOLD_DIM, marginLeft: 'auto' }}>
                      {data.totalXp} XP
                    </span>
                  )}
                  <span style={{ fontSize: fontSize - 3, color: SILVER, marginLeft: isEnd ? 0 : 'auto' }}>
                    {new Date(evt.createdAt).toLocaleDateString()}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── Shared UI components ─────────────────────────────────────

function Card({ title, icon, fontSize, children }: {
  title: string; icon: IconName; fontSize: number; children: React.ReactNode;
}) {
  return (
    <div style={{
      background: BG_CARD, borderRadius: 6,
      border: `1px solid ${BORDER}`, overflow: 'hidden',
    }}>
      <div style={{
        padding: '8px 12px',
        borderBottom: `1px solid ${BORDER}`,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <Icon name={icon} size={13} color={GOLD} />
        <span style={{ fontFamily: 'Georgia, serif', fontSize, color: '#e0d5c0', fontWeight: 'bold' }}>
          {title}
        </span>
      </div>
      <div style={{ padding: 12 }}>
        {children}
      </div>
    </div>
  );
}

function LoreLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 10, fontFamily: 'Georgia, serif',
      color: GOLD_DIM, textTransform: 'uppercase',
      letterSpacing: '0.5px', fontWeight: 'bold',
    }}>
      {children}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────

function pStyle(fontSize: number): React.CSSProperties {
  return {
    fontFamily: 'Georgia, serif',
    fontSize: fontSize - 1,
    color: 'rgba(192, 192, 192, 0.85)',
    lineHeight: 1.5,
    margin: 0,
  };
}

function repColor(tier: string): string {
  switch (tier) {
    case 'allied': return '#4caf50';
    case 'friendly': return '#8bc34a';
    case 'neutral': return SILVER;
    case 'unfriendly': return '#ff9800';
    case 'hostile': return '#f44336';
    default: return SILVER;
  }
}

function npcStatusColor(status: string): string {
  switch (status) {
    case 'active': return '#8bc34a';
    case 'allied': return '#4caf50';
    case 'dead': return '#f44336';
    case 'missing': return '#ff9800';
    case 'inactive': return SILVER;
    default: return SILVER;
  }
}
